/* The decision the whole tick model rests on, kept free of the socket and the store so it can
be tested. RoundHandler imports wsClient, which pulls in the entire app graph. */

import { elapsedSecondsFromDateStr } from "src/utils/dates"

/* The default a game falls back to when it predates the configurable clock (2026-08-21). Every
function below asks the game first: the lobby's host picks the length, the server stores it on the
game, and client and server must judge by the same number. */
export const ROUND_SECONDS = 60

/* What the lobby offers (owner, 2026-09-22): from a brisk half minute to a week. The clock is
the same rule at every length, so a week is one more option and not a mode. The server's
`MAX_ROUND_SECONDS` is the week, and it is the ceiling for a reason that lives here: the deadline
is a `setTimeout`, which overflows past 24.8 days and fires at once. */
export const ROUND_LENGTH_OPTIONS = [30, 60, 120, 300, 600, 3600, 86400, 604800]

export const roundSecondsOf = (game) => Number(game?.roundSeconds) || ROUND_SECONDS

/* How long after the deadline a player who is NOT the host waits before turning the round
itself. The host fires the moment the round is overdue; a connected host therefore always gets
there first, and the seconds are what keep two browsers from racing the same tick every round.
Whoever turns an overdue round while the host is away takes the clock (the server's rule). */
export const NON_HOST_GRACE_SECONDS = 3

/* The automatic round turn is a guess made on this browser's clock, and the server may still
disagree by a moment: the round is not over yet, or it is not this seat's to turn. Nobody pressed
anything, so nobody should be told (2026-09-28). The timer notes when it sent, and a round-turn
refusal arriving within this window is read as its answer and not shown. A NEXT ROUND the player
pressed themselves is not noted, and its refusal still shows. */
const AUTO_ADVANCE_ANSWER_MS = 5000
// GamePaused too (owner, 2026-09-29): a pause that lands while the timer is trying is not news to
// anybody, and the pause notice in the bar already says so.
// GameNotPaused as well (security audit, 2026-10-25): the lapsed pause's automatic resume is sent
// by more than one seat, and every one after the first finds the clock already running.
const ROUND_TURN_REFUSALS = new Set(["NotTheHost", "GameNotAbleToGoNextRound", "GamePaused", "GameNotPaused"])
let lastAutoAdvanceAt = -Infinity

export const noteAutoAdvance = (now = Date.now()) => {
  lastAutoAdvanceAt = now
}

export const answersAutoAdvance = (refusalName, now = Date.now()) =>
  ROUND_TURN_REFUSALS.has(refusalName) && now - lastAutoAdvanceAt <= AUTO_ADVANCE_ANSWER_MS

/* How often the timer tries again once the round is overdue. A try the server refused (its
clock a second behind ours) used to wait a whole round for the next one. */
export const AUTO_ADVANCE_RETRY_MS = 5000

const overdueBySeconds = (game) => {
  // tickTime is null on the very first round; treat that as "no deadline yet" rather than letting
  // `new Date(null)` resolve to 1970 and report the round as aeons overdue.
  if (!game?.tickTime) return -Infinity
  return elapsedSecondsFromDateStr(game.tickTime) - roundSecondsOf(game)
}

const mySeat = (game, userId) => game?.players?.find((player) => player.user.id === userId)

// Only players still in the game gate the round (owner's R2, 2026-09-03). The server's gate has
// skipped eliminated players all along; counting every row meant that once a bot was eliminated —
// it never readies again — the host waited out the clock every round.
const everybodyStillPlayingIsReady = (game) => {
  const stillPlaying = (game?.players || []).filter((player) => !player.eliminated)
  return stillPlaying.length > 0 && stillPlaying.every((player) => player.ready)
}

/* Whether this client should advance the round now.

Pure and exported so it can be tested: this is the decision the entire tick model rests on, and it
used to live inline where nothing could reach it. Being host means one thing: this browser turns
the round on its own while it is connected — when everybody is ready, or at the second zero. A
round with time left is the host's alone, and a game nobody is connected to does not advance, by
design. Once the round is overdue, any seated player still in the fight turns it (owner,
2026-09-22), a few seconds behind the host so a host who is here is never raced, and the server
makes whoever turns it host if the host's socket is down. One rule at every round length: a
minute or a week.

And a host whose socket is down sees nobody ready (owner, 2026-09-29: "If the host is disconnected
and ready, we should be able to go to next round"): with everybody ready and the host known to be
away, a seated player still in the fight turns the round at once, without waiting out the day, and
the server hands them the clock. RoundHandler holds that try back by NON_HOST_GRACE_SECONDS, like
the overdue one. A spectator host has no seat to be absent from, so this never races one. */
export const shouldAdvanceRound = ({ connected, userId, game }) => {
  if (!connected || !game) return false
  if (game.status === "finished") return false
  // A paused game waits for a resume, however ready or overdue it is.
  if (game.pausedBy) return false

  const overdueBy = overdueBySeconds(game)
  if (game.host?.id !== userId) {
    const seat = mySeat(game, userId)
    if (!seat || seat.eliminated) return false
    return overdueBy >= NON_HOST_GRACE_SECONDS || (everybodyStillPlayingIsReady(game) && hostDisconnected(game))
  }

  return everybodyStillPlayingIsReady(game) || overdueBy >= 0
}

/* Milliseconds until this client's moment to turn the round: the deadline for the host, the
deadline plus the grace for anybody else. Floored at 0 so a missed deadline fires immediately
rather than scheduling a negative timeout. */
export const millisecondsUntilDeadline = (game, { userId } = {}) => {
  const grace = userId !== undefined && game?.host?.id !== userId ? NON_HOST_GRACE_SECONDS : 0
  if (!game?.tickTime) return (roundSecondsOf(game) + grace) * 1000
  const remaining = roundSecondsOf(game) + grace - elapsedSecondsFromDateStr(game.tickTime)
  return Math.max(0, remaining * 1000) + 100
}

/* Whether the clock can be taken from the host, and how long until it can.

Mirrors `games/host.claim_host` on the server: the host's socket is down AND the round it owed is
overdue (the same minute it is granted to tick), or there was never a round to wait for. Pure so
RoundHandler can show the countdown from the same numbers the server will judge by. Since
2026-09-22 the taking is done by turning the round (`next_round` from a seated player hands the
clock to whoever turns an overdue round while the host is away), so these decide when the button
and the countdown show, not a separate claim. */
export const hostDisconnected = (game) => {
  const hostPlayer = game?.players?.find((p) => p.user.id === game.host?.id)
  return Boolean(hostPlayer) && hostPlayer.connected === false
}

export const secondsUntilHostClaim = (game) => {
  if (!game?.tickTime) return 0
  return Math.max(0, Math.ceil(roundSecondsOf(game) - elapsedSecondsFromDateStr(game.tickTime)))
}

/* With everybody ready there is nothing left to wait for, so the button shows at once (owner,
2026-09-29) — the server lets a seated player turn a ready round the absent host cannot see. */
export const canClaimHost = ({ userId, game }) => {
  if (!game || game.host?.id === userId || game.status === "finished") return false
  return hostDisconnected(game) && (secondsUntilHostClaim(game) === 0 || everybodyStillPlayingIsReady(game))
}

/* A pause ends by itself (security audit, 2026-10-25): the server lets it stand at most
`MAX_PAUSE_SECONDS` and publishes when it lapses as `paused_until`. There is no server clock, so
somebody has to say so — the host at that second, any other seat still in the fight
NON_HOST_GRACE_SECONDS later (the round turn's own rule) — by sending `resume_game`, which the
server answers as of the second the pause lapsed. Milliseconds until this seat should send it, or
null when it has nothing to send: no pause, no lapse published, or no seat in the fight. */
export const millisecondsUntilPauseLapses = (game, { userId } = {}) => {
  if (!game?.pausedBy || !game?.pausedUntil) return null
  if (game.status === "finished") return null
  const isHost = game.host?.id === userId
  if (!isHost) {
    const seat = mySeat(game, userId)
    if (!seat || seat.eliminated) return null
  }
  const grace = isHost ? 0 : NON_HOST_GRACE_SECONDS
  const remaining = grace - elapsedSecondsFromDateStr(game.pausedUntil)
  return Math.max(0, remaining * 1000) + 100
}

/* How many pauses this player has left of the server's `MAX_PAUSES_PER_PLAYER` (published under
`rules_config`), or null when the server publishes no budget. */
export const pausesLeft = (game, userId, maxPauses) => {
  if (!Number.isFinite(maxPauses)) return null
  const used = Number(game?.pausesUsed?.[String(userId)] ?? 0)
  return Math.max(0, maxPauses - used)
}
